import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Alert, Spinner, Badge, Form, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import ShopifyHeader from '../../ShopifyHeader';

const Categories = () => {
    const { shop } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [markupPercentage, setMarkupPercentage] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        loadCategories();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadCategories = async () => {
        try {
            setIsLoading(true);
            setError('');

            // Extract merchant name from shop domain
            const merchant = shop.replace('.myshopify.com', '');

            console.log(`Loading categories for merchant: ${merchant}`);

            // Call the real API endpoint
            const response = await fetch('https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/integration/settings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    merchant: merchant,
                    action: 'get_merchant_categories'
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            console.log('Categories API response:', result);

            if (result.success) {
                // Transform API data to match component expectations
                const transformedCategories = result.data.categories.map((category, index) => ({
                    id: index + 1, // Generate ID for table key
                    meli_category_id: category.meli_category_id,
                    meli_name: category.meli_name,
                    meli_domain_name: category.meli_domain_name,
                    markup_percentage: category.markup_percentage || 0,
                    product_count: category.product_count
                }));

                setCategories(transformedCategories);
            } else {
                throw new Error(result.error || 'Failed to load categories');
            }

        } catch (error) {
            console.error('Error loading categories:', error);
            setError(`Failed to load categories: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditMarkup = (category) => {
        setSelectedCategory(category);
        setMarkupPercentage(category.markup_percentage.toString());
        setShowModal(true);
    };

    const handleSaveMarkup = async () => {
        setIsSaving(true);
        if (selectedCategory && markupPercentage) {
            try {
                // Extract merchant name from shop domain
                const merchant = shop.replace('.myshopify.com', '');

                console.log(`Updating markup for category ${selectedCategory.meli_category_id} to ${markupPercentage}%`);

                // Call the API endpoint to update markup
                const response = await fetch('https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/integration/settings', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        merchant: merchant,
                        action: 'update_category_markup',
                        meli_category_id: selectedCategory.meli_category_id,
                        markup_percentage: parseFloat(markupPercentage)
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
                }

                const result = await response.json();
                console.log('Update markup API response:', result);

                if (result.success) {
                    // Update local state
                    setCategories(categories.map(cat =>
                        cat.id === selectedCategory.id
                            ? { ...cat, markup_percentage: parseFloat(markupPercentage) }
                            : cat
                    ));

                    // Show success message (optional)
                    console.log(result.message);

                    setShowModal(false);
                    setSelectedCategory(null);
                    setMarkupPercentage('');
                } else {
                    throw new Error(result.error || 'Failed to update markup');
                }

            } catch (error) {
                console.error('Error updating markup:', error);
                setError(`Failed to update markup: ${error.message}`);
                // Keep modal open on error so user can retry
            } finally {
                setIsSaving(false);
            }
        }
    };



    if (isLoading) {
        return (
            <div style={{ minHeight: '100vh' }}>
                <ShopifyHeader />
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" />
                        <h5 className="mt-3">Loading Categories...</h5>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh' }}>
            <ShopifyHeader />

            <Container className="py-4">
                <Row>
                    <Col>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>

                            </div>
                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate(`/shopify/${shop}/settings`)}
                            >
                                <i className="fas fa-arrow-left me-2"></i>
                                Back to Settings
                            </Button>
                        </div>
                    </Col>
                </Row>

                {error && (
                    <Row className="mb-4">
                        <Col>
                            <Alert variant="danger">
                                {error}
                            </Alert>
                        </Col>
                    </Row>
                )}

                <Row>
                    <Col>
                        <Card>
                            <Card.Header>
                                <h5 className="mb-0">Categories</h5>
                            </Card.Header>
                            <Card.Body>
                                {categories.length === 0 ? (
                                    <div className="text-center py-4">
                                        <p className="text-muted">No categories found for this merchant.</p>
                                        <Button variant="primary" onClick={loadCategories}>
                                            Refresh
                                        </Button>
                                    </div>
                                ) : (
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>Category Name</th>
                                                <th>MELI ID</th>
                                                <th>Markup %</th>
                                                <th>Products</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {categories.map((category) => (
                                                <tr key={category.id}>
                                                    <td>
                                                        <div>
                                                            <strong>{category.meli_name || 'Unknown Category'}</strong>
                                                            <br />
                                                            <small className="text-muted">{category.meli_domain_name || 'No domain'}</small>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <code>{category.meli_category_id || 'N/A'}</code>
                                                    </td>
                                                    <td>
                                                        <Badge bg="info">
                                                            {category.markup_percentage}%
                                                        </Badge>
                                                    </td>

                                                    <td>
                                                        <Badge bg="secondary">
                                                            {category.product_count} products
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleEditMarkup(category)}
                                                        >
                                                            <i className="fas fa-edit me-1"></i>
                                                            Edit Markup %
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Edit Markup Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Markup Percentage</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCategory && (
                        <div>
                            <p><strong>Category:</strong> {selectedCategory.meli_name}</p>
                            <p><strong>MELI ID:</strong> {selectedCategory.meli_category_id}</p>
                            <Form.Group className="mb-3">
                                <Form.Label>Markup Percentage</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="1"
                                    min="0"
                                    max="100"
                                    value={markupPercentage}
                                    onChange={(e) => setMarkupPercentage(e.target.value)}
                                    placeholder="Enter markup percentage"
                                    style={{ width: '100px' }}
                                />
                                <Form.Text className="text-muted">
                                    Enter the markup percentage to be applied to products in this category
                                </Form.Text>
                            </Form.Group>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary"
                        disabled={isSaving}
                        onClick={handleSaveMarkup}>
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Categories;
